import { useState, useEffect, useCallback, useRef } from 'react';
import { useCompanyStore } from '../stores/companyStore';
import { authAPI, companiesAPI } from '../utils/api';
import { notify } from '../stores/notificationStore';
import type { Company } from '../types/models';  // ✅ AJOUTÉ: Import Company V2
import { logger } from '../utils/logger';
import { cleanCompanyPayload } from '../utils/payloadUtils';

/**
 * Hook personnalisé pour gérer les paramètres de l'entreprise
 * Architecture 3 couches: Component → Hook → API Client → HTTP
 *
 * Responsabilités:
 * - Chargement des données de l'entreprise
 * - Sauvegarde des modifications
 * - Gestion de l'état du formulaire
 * - Gestion des erreurs et du loading
 */
export const useCompanySettings = () => {
  const { company: contextCompany, loadCompany, setCompany: updateCompanyInStore } = useCompanyStore();

  const [company, setCompany] = useState<Company | null>(null);  // ✅ CORRIGÉ: any → Company | null
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string>('');

  // Flag pour éviter les chargements multiples
  const hasLoadedRef = useRef(false);

  /**
   * Effet pour synchroniser avec contextCompany
   */
  useEffect(() => {
    if (contextCompany) {
      setCompany(contextCompany);
      setLoading(false);
      hasLoadedRef.current = true;
    }
  }, [contextCompany]);

  /**
   * Effet pour charger les données au montage du composant UNIQUEMENT
   */
  useEffect(() => {
    // Ne charger qu'une seule fois au montage
    if (hasLoadedRef.current) return;

    const loadInitialData = async () => {
      try {
        setLoading(true);
        setError('');

        // Si société déjà en contexte, l'utiliser
        if (contextCompany) {
          setCompany(contextCompany);
          hasLoadedRef.current = true;
          return;
        }

        // Sinon, charger depuis l'API utilisateur
        const data = await authAPI.me();
        logger.log('Données utilisateur:', data);

        if (data.companyId) {
          await loadCompany(data.companyId);
          // contextCompany sera mis à jour par le store
          // et le premier useEffect le synchronisera
        } else {
          setError('Aucune société associée à votre compte. Veuillez contacter l\'administrateur.');
        }
      } catch (err) {
        logger.error('Erreur lors du chargement:', err);
        setError(err instanceof Error ? err.message : 'Erreur de connexion au serveur');
      } finally {
        setLoading(false);
      }
    };

    loadInitialData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Dépendances vides = exécution une seule fois au montage

  /**
   * Met à jour un champ de l'entreprise dans l'état local
   */
  const updateField = useCallback((field: string, value: string | number) => {
    setCompany((prev) => prev ? { ...prev, [field]: value } : null);  // ✅ CORRIGÉ: any supprimé, vérification null
  }, []);

  /**
   * Sauvegarde les modifications de l'entreprise
   */
  const saveCompany = useCallback(async () => {
    if (!company?.id) {
      notify.error('Aucune société à sauvegarder', 'Erreur');
      return false;
    }

    try {
      setSaving(true);

      // Préparer le payload avec les conversions de types nécessaires
      const preparedCompany = {
        ...company,
        // Convertir capital en nombre (les inputs HTML retournent des strings)
        capital: company.capital ? Number(company.capital) : undefined,
        // Convertir defaultPaymentTermsDays en nombre si présent
        defaultPaymentTermsDays: company.defaultPaymentTermsDays
          ? Number(company.defaultPaymentTermsDays)
          : undefined,
      };

      // Nettoyer le payload avant envoi (supprime null et chaînes vides)
      const cleanedPayload = cleanCompanyPayload(preparedCompany as unknown as Record<string, unknown>);
      logger.log('Payload nettoyé pour sauvegarde:', cleanedPayload);

      // Appel API pour sauvegarder
      await companiesAPI.update(company.id, cleanedPayload);

      // Mettre à jour le store
      updateCompanyInStore(company);

      notify.success('Paramètres sauvegardés avec succès !', 'Sauvegarde réussie');
      return true;
    } catch (error) {
      logger.error('Erreur lors de la sauvegarde:', error);
      notify.error('Erreur : ' + (error as Error).message, 'Erreur de sauvegarde');
      return false;
    } finally {
      setSaving(false);
    }
  }, [company, updateCompanyInStore]);

  /**
   * Recharge les données de l'entreprise
   */
  const reload = useCallback(async () => {
    hasLoadedRef.current = false;
    setLoading(true);

    try {
      const data = await authAPI.me();
      if (data.companyId) {
        await loadCompany(data.companyId);
      }
    } catch (err) {
      logger.error('Erreur lors du rechargement:', err);
      setError(err instanceof Error ? err.message : 'Erreur de rechargement');
    } finally {
      setLoading(false);
    }
  }, [loadCompany]);

  return {
    // État
    company,
    loading,
    saving,
    error,

    // Actions
    updateField,
    saveCompany,
    reload,
  };
};
